// Number / currency / relative-time formatters that respect the
// operator's selected language. Built on `Intl.*` which does flow
// through cleanly when you pass it the language tag.

import i18n from './index';

function currentLng(): string {
  return (i18n.language || 'en');
}

/** Format an integer / float per the operator's locale. Hebrew uses
 *  Western Arabic digits (0123456789) the same as English, so the
 *  visible difference is mostly thousands-separator + decimal marker. */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(currentLng(), options).format(value);
}

/** Format a currency amount. Defaults to USD; pass `currency` for
 *  ILS / EUR / etc. */
export function formatCurrency(
  value: number,
  currency: string = 'USD',
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(currentLng(), {
    style: 'currency',
    currency,
    ...options,
  }).format(value);
}

/** Format relative time ("3 minutes ago" / "in 2 hours") using the
 *  browser's RelativeTimeFormat. Pass a signed difference in seconds:
 *  past times are negative, future times positive. Picks the most
 *  appropriate unit automatically. */
export function formatRelativeTime(deltaSeconds: number): string {
  const rtf = new Intl.RelativeTimeFormat(currentLng(), { numeric: 'auto' });
  const abs = Math.abs(deltaSeconds);
  if (abs < 60) return rtf.format(Math.round(deltaSeconds), 'second');
  if (abs < 3600) return rtf.format(Math.round(deltaSeconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(deltaSeconds / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(deltaSeconds / 86400), 'day');
  if (abs < 31536000) return rtf.format(Math.round(deltaSeconds / 2592000), 'month');
  return rtf.format(Math.round(deltaSeconds / 31536000), 'year');
}

/** Locale-aware "2 hours ago" from an ISO/Date. The scannable form for feed
 *  columns (incidents, tickets, alarms). Returns '' for missing/invalid input
 *  so callers can fall back. */
export function timeAgo(value: string | number | Date | null | undefined): string {
  if (value == null) return '';
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (!Number.isFinite(ms)) return '';
  return formatRelativeTime((ms - Date.now()) / 1000);
}
